import { useEffect, useRef, useState } from 'react';

import { useAuthStore } from '@/features/auth/auth-store';
import { SWITCHABLE_ROLES, type DisplayRole } from '@/features/auth/roles';
import { useDisplayRole } from '@/features/auth/useDisplayRole';
import { cn } from '@/lib/utils';

/**
 * The v7 role switcher (SCRUM-387) — the TopNav gold mono chip is an
 * interactive dropdown with a SWITCH ROLE section (5 roles, active check) and
 * an ACCOUNT STATE section (Unassigned, "NO ROLE YET" hint). Chevron rotates
 * 180° while open; options hover to bg-3; the active option is gold with a ✓.
 *
 * PRESENTATION AFFORDANCE (SCRUM-576): switching swaps the VIEWED role
 * client-side only (auth-store setDemoRole → sessionStorage); it does NOT change
 * server-side authorization.
 *
 * NEUTRALIZED UNDER REAL AUTH (SCRUM-103, D6): when a real SSO session is present
 * (`accessToken != null` — set only by completeSsoCallback), the role comes from
 * the validated JWT claim and the server enforces module access (403 on an
 * ungranted role). Letting the demo switcher swap the VIEWED role then would be
 * dishonest — the nav would show a role the token doesn't actually hold, while
 * every gated API call still 403s on the real role. So under real auth the chip
 * renders STATIC (no dropdown): it displays the authenticated role and cannot be
 * changed. In the token-less demo build (no accessToken) it stays fully
 * interactive so the product can be shown from each role's perspective. The
 * server 403 (AC4/SC8) is the backstop regardless of this control.
 */
export function RoleSwitcher() {
  const user = useAuthStore((s) => s.user);
  const setDemoRole = useAuthStore((s) => s.setDemoRole);
  const accessToken = useAuthStore((s) => s.accessToken);
  const displayName = useDisplayRole();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const role = user?.role ?? 'Bank Executive';

  // Real authenticated session → the switcher is display-only (see remarks).
  const realAuth = accessToken != null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (next: DisplayRole) => {
    setDemoRole(next);
    setOpen(false);
  };

  const sectionLabel =
    'text-text-3 px-3 pt-2 pb-1 font-mono text-[0.5625rem] font-bold tracking-[0.08em] uppercase';

  // Under real auth: a static, non-interactive chip showing the authenticated role.
  // No dropdown, no chevron — the role is fixed by the JWT and enforced server-side.
  if (realAuth) {
    return (
      <div className="select-none">
        <span
          aria-label="Active role"
          className={cn(
            'bg-brand-dim text-brand-primary rounded-tk-sm inline-flex items-center gap-[5px] border border-[var(--border-accent)] py-[3px] pr-2 pl-2.5 font-mono text-[0.6875rem] font-semibold tracking-[0.02em]',
          )}
        >
          {displayName(role)}
        </span>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative select-none">
      <button
        type="button"
        aria-label="Active role"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className={cn(
          'bg-brand-dim text-brand-primary rounded-tk-sm inline-flex items-center gap-[5px] border border-[var(--border-accent)] py-[3px] pr-2 pl-2.5 font-mono text-[0.6875rem] font-semibold tracking-[0.02em] transition-shadow',
          'hover:shadow-tk-sm cursor-pointer',
        )}
      >
        {displayName(role)}
        <svg
          viewBox="0 0 24 24"
          width="10"
          height="10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          aria-hidden="true"
          className={cn('transition-transform duration-150', open && 'rotate-180')}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Switch role"
          className="bg-bg-4 border-border-1 rounded-tk-md shadow-tk-md absolute top-[calc(100%+4px)] left-0 z-[100] min-w-[200px] border py-1"
        >
          <p className={sectionLabel}>Switch Role</p>
          {SWITCHABLE_ROLES.map((r) => {
            const active = r === role;
            return (
              <button
                key={r}
                type="button"
                role="menuitem"
                onClick={() => {
                  choose(r);
                }}
                className={cn(
                  'hover:bg-bg-3 hover:text-text-0 flex w-full items-center justify-between px-3 py-1.5 text-left text-[0.8125rem] transition-colors',
                  active ? 'text-brand-primary font-medium' : 'text-text-1',
                )}
              >
                {displayName(r)}
                {active && (
                  <span aria-hidden="true" className="text-brand-primary text-xs">
                    ✓
                  </span>
                )}
              </button>
            );
          })}

          <div className="bg-border-1 my-1 h-px" aria-hidden="true" />
          <p className={cn(sectionLabel, 'opacity-85')}>Account State</p>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              choose('Unassigned');
            }}
            className={cn(
              'hover:bg-bg-3 hover:text-text-0 flex w-full items-center justify-between gap-4 px-3 py-1.5 text-left text-[0.8125rem] transition-colors',
              role === 'Unassigned' ? 'text-brand-primary font-medium' : 'text-text-2',
            )}
          >
            Unassigned
            <span
              className={cn(
                'font-mono text-[0.5625rem] tracking-[0.04em] uppercase',
                role === 'Unassigned' ? 'text-brand-primary' : 'text-text-3',
              )}
            >
              No role yet
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
